import {customElement, property} from 'lit/decorators.js';
import {css, CSSResult, html, LitElement, nothing, PropertyValues, unsafeCSS} from 'lit';
import {ifDefined} from 'lit/directives/if-defined.js';
import {formatImageUrl, getPictureSourceSet} from '../../../scripts/service/image-formatter.js';
import styles from './wishlist-image.scss';

@customElement('ents-wishlist-image')
export class WishlistImage extends LitElement {
  @property({type: String, attribute: 'image-url'})
  private imageUrl: string = '';

  @property({type: String, attribute: 'alt-text'})
  private altText: string = '';

  @property({type: Boolean, attribute: 'is-beauty', reflect: true})
  private isBeauty: boolean = false;

  @property({type: Boolean, attribute: 'in-swiper', reflect: true})
  private inSwiper: boolean = false;

  @property({type: String, attribute: 'brand-code', reflect: true})
  private brandCode: string = '';

  @property({type: String, attribute: 'sizes', reflect: true})
  private sizes?: string;

  @property({type: String, attribute: 'loading'})
  private loading: 'lazy' | 'eager' = 'lazy';

  @property({type: Object, hasChanged: () => false})
  onImageLoad?: (event: Event) => void;

  // Disable Shadow DOM to allow parent styles to apply
  createRenderRoot() {
    return this.inSwiper ? this : super.createRenderRoot();
  }

  static get styles(): CSSResult {
    return css`${unsafeCSS(styles)}`;
  }

  render() {
    return html`
      <div
        class="ents-wishlist-image__wrapper"
        role=${ifDefined(this.altText ? 'img' : undefined)}
        alt=${ifDefined(this.altText ? this.altText : undefined)}
        aria-labelledby=${ifDefined(this.altText ? 'wishlist-image-alt-text' : undefined)}>
        <picture aria-hidden="true" data-test-ents-wishlist-picture>
          <source
            srcset="${getPictureSourceSet(this.imageUrl, 'webp')}"
            sizes=${ifDefined(this.sizes)}
            type="image/webp">
          ${this.onImageLoad ?
            html`<img
              src="https://cms.brnstc.de/product_images/287x393_retina${formatImageUrl(this.imageUrl, 'jpg')}"
              loading="${this.loading}"
              alt="${this.altText}"
              @load="${(e: Event) => this.handleImageLoad(e)}">` :
            html`<img
              src="https://cms.brnstc.de/product_images/287x393_retina${formatImageUrl(this.imageUrl, 'jpg')}"
              loading="${this.loading}"
              alt="${this.altText}">`
          }
        </picture>
        <ents-wishlist-product-image-overlay
          ?is-beauty="${this.isBeauty}"
          brand-code="${this.brandCode}"
          data-test-ents-wishlist-image-overlay
        ></ents-wishlist-product-image-overlay>
      </div>
      ${this.altText ? html`
          <span id="wishlist-image-alt-text" class="sr-only" aria-hidden="true">${this.altText}</span>` :
        nothing}
    `;
  }

  shouldUpdate(changedProperties: PropertyValues): boolean {
    if(changedProperties.size === 0){
      return false;
    }
    return super.shouldUpdate(changedProperties);
  }

  private handleImageLoad(event: Event) {
    if (this.onImageLoad) {
      this.onImageLoad(event);
    }
  }
}
